import type {Solution} from '@/ts/types/dto/interventions-dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';

/** Wraps a user's answer payload with an optional reference to the answered component */
export interface ComponentAnswer<T> {
  answer: T
  userComponentId?: UuidDTO; // is optional when submitting homework, so the key should be absent
}

/** Types of answerable components within a session */
export enum AnswerComponentType {
  QUESTIONNAIRE = 'questionnaire',
  JOURNAL = 'journal',
  CONCEPTUALIZATION_WORKSHEET = 'conceptualization_worksheet',
  PROBLEM_SOLVING_WORKSHEET = 'problem_solving_worksheet',
}

/** Session answer submission containing questionnaire responses */
export interface SessionRequestQuestionnaire {
  type: AnswerComponentType.QUESTIONNAIRE
  questionnaireAnswers: QuestionnaireAnswers
}

/** Session answer submission containing a journal entry */
export interface SessionRequestJournal {
  type: AnswerComponentType.JOURNAL;
  answerJournal: AnswerJournal;
}

/** Session answer submission containing a completed conceptualization worksheet */
export interface SessionRequestConceptualizationWorksheet {
  type: AnswerComponentType.CONCEPTUALIZATION_WORKSHEET
  answerConceptualizationWorksheet: AnswerConceptualizationWorksheet
}

/** Session answer submission containing a completed problem-solving worksheet */
export interface SessionRequestProblemSolvingWorksheet {
  type: AnswerComponentType.PROBLEM_SOLVING_WORKSHEET
  answerProblemSolvingWorksheet: AnswerProblemSolvingWorksheet
}

/** Answer input types for questionnaire questions */
export enum QuestionnaireType {
  MULTICHOICE = 'multichoice',
  NUMERIC = 'numeric',
}

/** Answer body for a numeric-type question with an optional free-text custom answer */
export interface QuestionAnswerNumericBody {
  numeric: number;
  type: QuestionnaireType.NUMERIC;
  customAnswer?: string;
}

/** Answer body for a multiple-choice question with selected option IDs and optional custom answer */
export interface QuestionAnswerMultiChoiceBody {
  multiChoice: UuidDTO[];
  type: QuestionnaireType.MULTICHOICE;
  customAnswer?: string;
}

/** DTO type for QuestionAnswerBody */
export type QuestionAnswerBody =
  QuestionAnswerMultiChoiceBody
  | QuestionAnswerNumericBody;


/** Submitted problem-solving worksheet data: problem, goal, rewards, and proposed solutions */
export interface AnswerProblemSolvingWorksheetBody {
  problem: string
  goal: string
  rewards: string[];
  solutions: Solution[]
}

/** Submitted conceptualization worksheet capturing a situation, feelings, thoughts, behaviors, and coping strategies */
export interface AnswerConceptualizationWorksheetBody {
  situation: string
  questionnaireAnswers: QuestionnaireAnswers[]
  primaryFeelingsIds: UuidDTO[]
  secondaryFeelingsIds: UuidDTO[]
  thoughts: string[]
  behaviors: string[]
  physicalReactions: string[]
  thinkingTrapsIds: UuidDTO[]
  copingThoughts: string[]
}

/** Submitted journal entry containing the user's free-text answer */
export interface AnswerJournalBody {
  answer: string;
}

/** A single question's answer within a questionnaire, linked to its component and question IDs */
export interface QuestionnaireAnswers<T extends QuestionAnswerBody = QuestionAnswerBody> {
  answers: T;
  userComponentId?: UuidDTO;
  questionId?: UuidDTO;
  questionnaireId?: UuidDTO;
}

/** DTO type for AnswerJournal */
export type AnswerJournal = ComponentAnswer<AnswerJournalBody>

/** DTO type for AnswerProblemSolvingWorksheet */
export type AnswerProblemSolvingWorksheet = ComponentAnswer<AnswerProblemSolvingWorksheetBody>;

/** DTO type for AnswerConceptualizationWorksheet */
export type AnswerConceptualizationWorksheet = ComponentAnswer<AnswerConceptualizationWorksheetBody>;

/** DTO type for SessionRequest */
export type SessionRequest =
  SessionRequestConceptualizationWorksheet
  | SessionRequestJournal
  | SessionRequestProblemSolvingWorksheet
  | SessionRequestQuestionnaire;

/** Request body for submitting a session's read status and all component answers */
export interface PutSessionRequest {
  isContentRead: boolean;
  answers: SessionRequest[];
}

